import { Button, Card, Group, Overlay, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import type { Message } from '../types/message.types';
import { ChatBody } from './ChatBody';
import { ChatFooter } from './ChatFooter';
import { ChatHeader } from './ChatHeader';

export interface ChatContainerProps {
  status?: 'idle' | 'active' | 'closed';
  messages: Message[];
  messageCount?: number;
  isRestored?: boolean;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onMinimize?: () => void;
  onEndConversation?: () => void;
  disabled?: boolean;
  emptyStateMessage?: string;
}

export function ChatContainer({
  status = 'idle',
  messages,
  messageCount,
  isRestored = false,
  inputValue,
  onInputChange,
  onSendMessage,
  onKeyPress,
  onMinimize,
  onEndConversation,
  disabled = false,
  emptyStateMessage,
}: ChatContainerProps) {
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);

  const handleCloseClick = () => {
    setShowEndConfirmation(true);
  };

  const handleConfirmEnd = () => {
    setShowEndConfirmation(false);
    if (onEndConversation) {
      onEndConversation();
    }
  };

  const handleCancelEnd = () => {
    setShowEndConfirmation(false);
  };

  return (
    <Card withBorder shadow="lg" radius="lg" w={400} h={600} pos="relative">
      <ChatHeader
        status={status}
        messageCount={messageCount ?? messages.length}
        isRestored={isRestored}
        onMinimize={onMinimize}
        onClose={onEndConversation ? handleCloseClick : undefined}
      />
      <ChatBody messages={messages} emptyStateMessage={emptyStateMessage} />
      <ChatFooter
        value={inputValue}
        onChange={onInputChange}
        onSend={onSendMessage}
        onKeyPress={onKeyPress}
        disabled={disabled}
      />

      {showEndConfirmation && (
        <Overlay
          color="#000"
          backgroundOpacity={0.65}
          blur={2}
          zIndex={1}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Card
            withBorder
            shadow="xl"
            padding="lg"
            radius="md"
            style={{ maxWidth: 320 }}
          >
            <Stack gap="md">
              <Text fw={700} size="lg">
                End Conversation?
              </Text>
              <Text size="sm">
                Are you sure you want to end this conversation? This action
                cannot be undone.
              </Text>
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={handleCancelEnd}>
                  Cancel
                </Button>
                <Button color="red" onClick={handleConfirmEnd}>
                  End Conversation
                </Button>
              </Group>
            </Stack>
          </Card>
        </Overlay>
      )}
    </Card>
  );
}
